import { basePath } from "@Common/constants";
import { PublicFooter } from "@Src/components/PublicFooter";
import { PublicNav } from "@Src/components/PublicNav";
import { RetrowaveBackground } from "@Src/components/RetrowaveBackground";
import CheckIcon from "@mui/icons-material/Check";
import {
	Box,
	Button,
	Container,
	Grid,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

interface PricingTier {
	name: string;
	price: string;
	description: string;
	features: string[];
	cta: string;
	highlighted?: boolean;
}

const pricingTiers: PricingTier[] = [
	{
		name: "Free",
		price: "0€",
		description: "Perfect for testing",
		features: ["Up to 1 form creation", "Up to 15 form responses per month"],
		cta: "Get Started",
	},
	{
		name: "Basic",
		price: "25€",
		description: "Ideal for individuals",
		features: [
			"Unlimited forms creation",
			"Up to 100 form responses per month",
		],
		cta: "Get Started",
	},
	{
		name: "Plus",
		price: "49€",
		description: "Ideal for small teams",
		features: [
			"Unlimited forms creation",
			"Up to 1000 form responses per month",
		],
		cta: "Get Started",
	},
	{
		name: "Pro",
		price: "89€",
		description: "Great fit for universities and companies",
		features: [
			"Unlimited forms creation",
			"Up to 10 000 form responses per month",
		],
		cta: "Get Started",
		highlighted: true,
	},
	{
		name: "Enterprise",
		price: "Custom",
		description:
			"For large organizations with specific needs (custom integrations, contract, specific compliance requirements...)",
		features: [],
		cta: "Contact Sales",
	},
];

export const PricingPage = () => {
	const navigate = useNavigate();

	const plans = pricingTiers.filter((p) => p.name !== "Enterprise");
	const enterprise = pricingTiers.find((p) => p.name === "Enterprise");

	return (
		<Box
			sx={{
				minHeight: "100vh",
				bgcolor: "background.default",
				position: "relative",
				overflow: "hidden",
			}}
		>
			<PublicNav />

			<RetrowaveBackground />

			<Container
				maxWidth="lg"
				sx={{ py: { xs: 8, md: 12 }, position: "relative", zIndex: 1 }}
			>
				{/* HEADER */}
				<Box sx={{ textAlign: "center", maxWidth: 700, mx: "auto", mb: 10 }}>
					<Typography variant="h2" fontWeight={700} mb={2}>
						Simple, Transparent Pricing
					</Typography>

					<Typography variant="h6" color="text.secondary">
						Choose the plan that fits your needs.
					</Typography>
				</Box>

				{/* PLANS GRID */}
				<Grid container spacing={4} alignItems="stretch">
					{plans.map((tier) => (
						<Grid item xs={12} sm={6} md={4} lg={3} key={tier.name}>
							<Paper
								sx={{
									height: "100%",
									p: 4,
									display: "flex",
									flexDirection: "column",
									borderRadius: 3,
									border: "2px solid",
									borderColor: tier.highlighted ? "primary.main" : "divider",
									position: "relative",
								}}
							>
								{tier.highlighted && (
									<Box
										sx={{
											position: "absolute",
											top: -12,
											left: "50%",
											transform: "translateX(-50%)",
											bgcolor: "primary.main",
											color: "white",
											px: 2,
											py: 0.5,
											borderRadius: 1,
											fontSize: 12,
											fontWeight: 600,
										}}
									>
										MOST POPULAR
									</Box>
								)}

								<Typography variant="h5" fontWeight={600}>
									{tier.name}
								</Typography>

								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ mt: 1, mb: 3 }}
								>
									{tier.description}
								</Typography>

								{/* PRICE */}
								<Box mb={3}>
									<Typography variant="h3" fontWeight={700}>
										{tier.price}
									</Typography>

									{tier.price !== "Custom" && (
										<Typography color="text.secondary">/month</Typography>
									)}
								</Box>

								{/* FEATURES */}
								<Stack spacing={1.5} flexGrow={1}>
									{tier.features.map((feature) => (
										<Box key={feature} sx={{ display: "flex", gap: 1 }}>
											<CheckIcon sx={{ color: "primary.main", fontSize: 20 }} />
											<Typography variant="body2" color="text.secondary">
												{feature}
											</Typography>
										</Box>
									))}
								</Stack>

								{/* CTA */}
								<Button
									variant={tier.highlighted ? "contained" : "outlined"}
									fullWidth
									size="large"
									sx={{ mt: 4, textTransform: "none", fontWeight: 600 }}
									onClick={() => navigate(basePath)}
								>
									{tier.cta}
								</Button>
							</Paper>
						</Grid>
					))}
				</Grid>

				<br />
				<br />

				{/* ENTERPRISE PLAN */}
				{enterprise && (
					<Paper
						sx={{
							mt: 8,
							p: 6,
							textAlign: "center",
							borderRadius: 3,
							border: "2px dashed",
							borderColor: "divider",
						}}
					>
						<Typography variant="h4" fontWeight={600} mb={2}>
							{enterprise.name}
						</Typography>

						<Typography color="text.secondary" mb={3}>
							{enterprise.description}
						</Typography>

						<Button
							variant="contained"
							size="large"
							onClick={() => navigate("/contact")}
						>
							{enterprise.cta}
						</Button>
					</Paper>
				)}

				{/* FOOTER CTA */}
				<Box sx={{ mt: 10, textAlign: "center" }}>
					<Typography color="text.secondary">
						Need help choosing?{" "}
						<Box
							component="button"
							onClick={() => navigate("/contact")}
							sx={{
								border: "none",
								background: "none",
								color: "primary.main",
								fontWeight: 600,
								cursor: "pointer",
							}}
						>
							Contact our sales team
						</Box>
					</Typography>
				</Box>
			</Container>

			<PublicFooter />
		</Box>
	);
};
